'use client'

import type { PageTypeProviderProps } from '@maestro/commerce-blocks/providers/page-type-provider/page-type-provider.types'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'

import { useCartContext } from '@maestro/commerce-blocks/context/cart.context'
import { useSessionReady } from '@maestro/commerce-blocks/providers/maestro-session/hooks/use-session-ready'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { PageType } from '@maestro/core'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { type EventPayloadType, EventType } from '@maestro/data-layer'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'

export const PageTypeProvider = (props: PageTypeProviderProps) => {
  const { id: propsId, isRenderedRecommendations = false, pageType } = props
  const pathname = usePathname()
  const urlSearchParams = useSearchParams()
  const { hash } = useHash()
  const { publish } = useEventBusContext()
  const { session } = useMaestroSession()
  const isSessionReady = useSessionReady()
  const pathPrevRef = useRef<string | null>(null)
  const hasInitialPublishRef = useRef(false)
  const { cart, isLoading } = useCartContext()

  const searchResultType = useMemo(() => {
    return pageType === PageType.SEARCH
      ? (urlSearchParams.get('type') ?? 'product')
      : undefined
  }, [pageType, urlSearchParams])

  const id = useMemo(() => {
    if (pageType === PageType.SEARCH) {
      return urlSearchParams.get('text') || propsId
    }

    return propsId
  }, [pageType, propsId, urlSearchParams])

  useEffect(() => {
    if (!isSessionReady) {
      return
    }

    const pathnamePrev = pathPrevRef.current
    const pathWithHash = `${pathname}${hash ? `#${hash}` : ''}`

    // Only track if this is a new path (but allow initial publish)
    if (pathWithHash === pathnamePrev && hasInitialPublishRef.current) {
      return
    }

    //if we are on cart -> wait until cart is loaded, before sending page view event
    if (pageType === PageType.CART && isLoading) {
      return
    }

    pathPrevRef.current = pathWithHash
    hasInitialPublishRef.current = true

    // Detect if hash changed by comparing previous and current hash
    const hashPrev = pathnamePrev?.split('#')[1] || ''
    const currentHash = hash || ''
    const isHashChanged = hashPrev !== currentHash

    const payload: EventPayloadType[EventType.PAGE_VIEW] = {
      pageType,
      id,
      isHashChanged,
      isRenderedRecommendations,
      session,
      hash: currentHash,
      cart:
        pageType === PageType.CART
          ? cart?.entries?.map((item) => item)
          : undefined,
      searchResultType,
    }

    publish<EventPayloadType[EventType.PAGE_VIEW]>(EventType.PAGE_VIEW, payload)
  }, [
    cart,
    hash,
    id,
    isLoading,
    isRenderedRecommendations,
    isSessionReady,
    pageType,
    pathname,
    publish,
    searchResultType,
    session,
  ])

  return null
}
